// EP-135 Tier 4 — MinutesPanel. Operator-side surface for the generated
// minutes document + dual-sign e-sign flow (§8 Q-5).
//
// The panel renders the DocumentBuilder payload inline (header +
// attendance + resolutions + certification sections) so the chair can
// walk through what they are about to sign. The actual PDF is built
// CLIENT-SIDE via pdfme (Tier 1 decision — never backend); this
// component is the OPERATOR-SIDE surface that POSTs the signed
// certificate_pdf_key back to the server after both signers stamp.
//
// Signing model:
//   - chair signs FIRST → chair_signed_at stamped (status still closed)
//   - secretary signs SECOND → secretary_signed_at stamped AND meeting
//     transitions to `certified` atomically + certificate_pdf_key
//     persisted (§8 Q-5 dual sign).
//   - either order is valid; the controller records which signer stamped
//     when via the per-role column.
//
// Reset:
//   - admin+ can clear a single signer's stamp BEFORE the meeting is
//     `certified`, allowing re-sign. Refused once certified — audit
//     trail is immutable.

import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { FileSignature, Lock, RotateCcw, ShieldCheck, FileText } from 'lucide-react'
import { Alert, Card, Button, Badge } from '@/components/ui'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import {
  useMeetingMinutes,
  useSignMinutes,
  useResetMinutesSignature,
} from '@/hooks/useGovernance'
import type { SigningRole } from '@/types/governance'

export interface MinutesPanelProps {
  meetingId: number
  /** True when the current viewer is allowed to stamp a signature (§8b — admin+). */
  canSign: boolean
  /** Customer id of the chair signer (operator picks from the meeting's committee). */
  chairCustomerId: number | null
  /** Customer id of the secretary signer. */
  secretaryCustomerId: number | null
}

export function MinutesPanel({
  meetingId,
  canSign,
  chairCustomerId,
  secretaryCustomerId,
}: MinutesPanelProps) {
  const { t } = useTranslation('governance')
  // The tally's decision is the winning choice (or a tie), never a pass/fail
  // verdict, so it renders through the choice labels; the literal branches
  // keep the i18n keys statically typed.
  const decisionLabel = (d: 'for' | 'against' | 'abstain' | 'tied' | null) => {
    if (d === null) return '—'
    if (d === 'tied') return t('votes.tally.tied')
    if (d === 'for') return t('votes.choices.for')
    if (d === 'against') return t('votes.choices.against')
    return t('votes.choices.abstain')
  }
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')
  // toLocaleString() follows the BROWSER locale and ignores the account's
  // timezone / date_format / calendar config (standing date rule).
  const formatDateTime = useDateTimeFormatter()

  const { data: minutes, isLoading, error } = useMeetingMinutes(meetingId)
  const sign = useSignMinutes(meetingId)
  const reset = useResetMinutesSignature(meetingId)

  const [signingRole, setSigningRole] = useState<SigningRole | null>(null)
  const [resettingRole, setResettingRole] = useState<SigningRole | null>(null)

  if (isLoading) {
    return (
      <Card title={t('minutes.title')} data-testid="minutes-panel-loading">
        <div className="flex items-center justify-center py-8 text-base-content/60">
          <FileText className="w-5 h-5 animate-pulse mr-2" />
          {t('meetings.empty.loading')}
        </div>
      </Card>
    )
  }

  if (error || !minutes) {
    return (
      <Card title={t('minutes.title')} data-testid="minutes-panel-error">
        <p className="text-sm text-error">{t('minutes.errors.not_ready')}</p>
      </Card>
    )
  }

  const cert = minutes.sections.find((s) => s.kind === 'certification') as
    | Extract<typeof minutes.sections[number], { kind: 'certification' }>
    | undefined

  const header = minutes.sections.find((s) => s.kind === 'header') as
    | Extract<typeof minutes.sections[number], { kind: 'header' }>
    | undefined
  const attendance = minutes.sections.find((s) => s.kind === 'attendance') as
    | Extract<typeof minutes.sections[number], { kind: 'attendance' }>
    | undefined
  const resolutions = minutes.sections.find((s) => s.kind === 'resolutions') as
    | Extract<typeof minutes.sections[number], { kind: 'resolutions' }>
    | undefined

  const isCertified = cert?.certified === true
  const chairSigned = !!cert?.chair?.signed_at
  const secretarySigned = !!cert?.secretary?.signed_at

  const handleSign = async (role: SigningRole) => {
    setSigningRole(role)
    const signerId = role === 'chair' ? chairCustomerId : secretaryCustomerId
    if (!signerId) {
      addToast({ type: 'error', message: t('minutes.errors.signer_not_found') })
      setSigningRole(null)
      return
    }
    try {
      await sign.mutateAsync({ role, signed_by_id: signerId })
      addToast({
        type: 'success',
        message:
          role === 'chair'
            ? t('minutes.messages.signed_chair')
            : t('minutes.messages.signed_secretary'),
      })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setSigningRole(null)
    }
  }

  const handleReset = async (role: SigningRole) => {
    setResettingRole(role)
    try {
      await reset.mutateAsync(role)
      addToast({
        type: 'success',
        message:
          role === 'chair'
            ? t('minutes.messages.reset_chair')
            : t('minutes.messages.reset_secretary'),
      })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setResettingRole(null)
    }
  }

  return (
    <Card
      title={t('minutes.title')}
      description={t('minutes.description')}
      bodyClassName="space-y-4"
      data-testid="minutes-panel"
    >
      {minutes.metadata.draft ? (
        <Alert
          variant="warning"
          icon={<FileSignature className="w-5 h-5" />}
          role="status"
          data-testid="minutes-draft-banner"
        >
          {t('minutes.draft_banner')}
        </Alert>
      ) : (
        <Alert
          variant="success"
          icon={<ShieldCheck className="w-5 h-5" />}
          role="status"
          data-testid="minutes-certified-banner"
        >
          {t('minutes.certified_banner')}
        </Alert>
      )}

      {header && (
        <section data-testid="minutes-section-header">
          <h3 className="font-semibold text-base mb-2">{t('minutes.sections.header')}</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><dt className="text-base-content/60">{t('meetings.labels.title')}</dt><dd>{header.title}</dd></div>
            <div><dt className="text-base-content/60">{t('meetings.labels.meeting_type')}</dt><dd>{header.meeting_type_label}</dd></div>
            <div><dt className="text-base-content/60">{t('meetings.labels.call_number')}</dt><dd>{header.call_number}</dd></div>
            <div><dt className="text-base-content/60">{t('meetings.labels.notice_lead_days')}</dt><dd>{header.notice_lead_days}</dd></div>
            {header.agenda && (
              <div className="sm:col-span-2"><dt className="text-base-content/60">{t('meetings.labels.agenda')}</dt><dd>{header.agenda}</dd></div>
            )}
          </dl>
        </section>
      )}

      {attendance && (
        <section data-testid="minutes-section-attendance">
          <h3 className="font-semibold text-base mb-2">{t('minutes.sections.attendance')}</h3>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('meetings.labels.title')}</th>
                  <th>Unit</th>
                  <th>{t('meetings.labels.status')}</th>
                  <th className="text-right">Weight</th>
                </tr>
              </thead>
              <tbody>
                {attendance.rows.map((row) => (
                  <tr key={row.id} data-testid="minutes-attendee-row">
                    <td>{row.customer_name}</td>
                    <td className="text-base-content/70">{row.unit_label ?? '—'}</td>
                    <td><Badge variant="count">{row.presence}</Badge></td>
                    <td className="text-right tabular-nums">{row.weight_snapshot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {resolutions && resolutions.rows.length > 0 && (
        <section data-testid="minutes-section-resolutions">
          <h3 className="font-semibold text-base mb-2">{t('minutes.sections.resolutions')}</h3>
          <ol className="space-y-3 list-decimal pl-5">
            {resolutions.rows.map((row) => (
              <li key={row.id} data-testid="minutes-resolution-row">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{row.title}</span>
                  {row.secret && <Lock className="w-3 h-3 text-base-content/60" />}
                  <Badge variant="count">{row.status}</Badge>
                </div>
                {row.body && <p className="text-sm text-base-content/70 mt-1">{row.body}</p>}
                <div className="text-xs text-base-content/60 mt-1">
                  {t('votes.tally.decision')}: {decisionLabel(row.tally.decision)}
                  {' · '}
                  {t('votes.tally.total_cast')}: {row.tally.total_cast}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {cert && (
        <section data-testid="minutes-section-certification">
          <h3 className="font-semibold text-base mb-2">{t('minutes.sections.certification')}</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="border border-base-300 rounded-md p-3">
              <dt className="text-base-content/60">{t('minutes.signing.chair_signed_at')}</dt>
              <dd className="mt-1">
                {cert.chair?.signed_at
                  ? formatDateTime(cert.chair.signed_at)
                  : t('minutes.signing.pending_chair')}
              </dd>
            </div>
            <div className="border border-base-300 rounded-md p-3">
              <dt className="text-base-content/60">{t('minutes.signing.secretary_signed_at')}</dt>
              <dd className="mt-1">
                {cert.secretary?.signed_at
                  ? formatDateTime(cert.secretary.signed_at)
                  : t('minutes.signing.pending_secretary')}
              </dd>
            </div>
            {cert.certificate_pdf_key && (
              <div className="sm:col-span-2">
                <dt className="text-base-content/60">{t('minutes.signing.pdf_key_label')}</dt>
                <dd className="font-mono text-xs break-all">{cert.certificate_pdf_key}</dd>
              </div>
            )}
          </dl>

          {canSign && !isCertified && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="minutes-sign-actions">
              {!chairSigned && (
                <Button
                  leftIcon={<FileSignature className="w-4 h-4" />}
                  onClick={() => handleSign('chair')}
                  loading={signingRole === 'chair' && sign.isPending}
                  disabled={sign.isPending || reset.isPending}
                  data-testid="btn-sign-chair"
                >
                  {t('minutes.actions.sign_chair')}
                </Button>
              )}
              {!secretarySigned && (
                <Button
                  variant="outline"
                  leftIcon={<FileSignature className="w-4 h-4" />}
                  onClick={() => handleSign('secretary')}
                  loading={signingRole === 'secretary' && sign.isPending}
                  disabled={sign.isPending || reset.isPending}
                  data-testid="btn-sign-secretary"
                >
                  {t('minutes.actions.sign_secretary')}
                </Button>
              )}
              {chairSigned && (
                <Button
                  variant="ghost"
                  leftIcon={<RotateCcw className="w-4 h-4" />}
                  onClick={() => handleReset('chair')}
                  loading={resettingRole === 'chair' && reset.isPending}
                  disabled={reset.isPending || sign.isPending}
                  data-testid="btn-reset-chair"
                >
                  {t('minutes.actions.reset_chair_signature')}
                </Button>
              )}
              {secretarySigned && (
                <Button
                  variant="ghost"
                  leftIcon={<RotateCcw className="w-4 h-4" />}
                  onClick={() => handleReset('secretary')}
                  loading={resettingRole === 'secretary' && reset.isPending}
                  disabled={reset.isPending || sign.isPending}
                  data-testid="btn-reset-secretary"
                >
                  {t('minutes.actions.reset_secretary_signature')}
                </Button>
              )}
            </div>
          )}

          {isCertified && (
            <p className="text-sm text-success mt-3" data-testid="minutes-both-signed">
              {t('minutes.signing.both_signed')}
            </p>
          )}
        </section>
      )}
    </Card>
  )
}
